import React, { use, useEffect, useState, useContext } from 'react';
import InvoiceActions from './Actions/InvoiceActions';
import Table from './InvoiceTable';
import {
  getInvoiceCSV,
  getInvoices,
  getSearch,
  updateInvoiceStatus,
} from '../../../services/InvoiceAPIs';
import {
  AutoComplete,
  Button,
  ConfigProvider,
  Input,
  message,
  Pagination,
  Spin,
} from 'antd';
import FiltersPane from './FiltersPane';
import colors from '../../../constants/ColourConstants';
import CustomButton from '../../common/Button/Button';
import {
  ExportOutlined,
  FilterOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { LOCK_MSG } from '../../../constants/ApiConstants';
import {
  createPayoutEvent,
  generatePayoutWithOverride,
} from '../../../services/PayoutAPIs';
import { ActionContext } from '../../common/Navigation/Navigation';

const getStatusType = (status) => {
  return (
    {
      'Contributor Approved': 'default',
      'Pending PM Approval': 'default',
      'Under Further Review': 'default',
      Voided: 'default',
      'Ready for Finance Review': 'default',
      'Pending Review': 'default',

      'Ready for Invoice': 'cyan',
      'Added to Invoice': 'cyan',
      'Ready for Payout': 'cyan',
      'Added to Payout': 'cyan',
      'Ready for Disbursement': 'cyan',
      'Payout Initiated': 'cyan',

      'Payout Pending': 'warning',
      'Ready for Resend': 'warning',

      'Payout Failure': 'error',

      'Payout Completed': 'success',
    }[status] || 'default'
  );
};

export const initFilters = {
  invoiceStatus: [],
  payoutStatus: [],
  invoiceLastModifiedDate: 'Last 30 days',
  invoiceCreationDate: 'Last 30 days',
  invoicePeriod: { month: [], year: '' },
  country: [],
  contributorEmail: '',
  notes: { type: 'Contains', value: '' },
};

export const monthOptions = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];
const currentYear = new Date().getFullYear();
export const yearOptions = Array.from({ length: 25 }, (_, i) => {
  const year = currentYear - i;
  return { value: year, label: year.toString() };
});

const Invoices = () => {
  const [invoices, setInvoices] = useState({});

  const [sortField, setSortField] = useState({});

  const [tableId, setTableId] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nullInvoicePeriodCount, setNullInvoicePeriodCount] = useState(0);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRecords, setselectedRecords] = useState([]);

  const [pageLoader, setPageLoader] = useState(true);
  const [tableLoader, setTableLoader] = useState(false);
  const [error, setError] = useState(false);

  const [openFilters, setOpenFilters] = useState(false);
  const [filterValues, setFilterValues] = useState(initFilters);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [createPayoutModalOpen, setCreatePayoutModalOpen] = useState(false);

  const [searchLoader, setSearchLoader] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [searchDropdown, setSearchDropdown] = useState(false);

  const [exportLoader, setExportLoader] = useState(false);
  const { setAction } = useContext(ActionContext);

  const fields = {
    invoiceId: 'ids',
    invoiceGrandTotal: 'amounts',
    createdAt: 'createdDates',
    updatedAt: 'modifiedDates',
  };

  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    !pageLoader && handleApplyFilters();
  }, [pageSize]);

  useEffect(() => {
    if (invoices[currentPage]) {
      setCurrentData(invoices[currentPage]);
    } else {
      handleInvoices();
    }
  }, [currentPage]);

  useEffect(() => {
    setCurrentData(invoices[currentPage]);
  }, [invoices]);

  const handlePageSizeChange = (_current, size) => setPageSize(size);
  const handleMessage = (type, content) => {
    messageApi.open({
      type: type,
      content: content,
      duration: 10,
    });
  };

  const handleApplySort = (filters) => {
    const updatedFilters = {
      ...filters,
      field: fields[filters.field],
    };

    setTableId(() => tableId + 1);
    setSelectedRowKeys(() => {
      return [];
    });
    setselectedRecords(() => {
      return [];
    });
    setInvoices(() => {
      return {};
    });
    setCurrentPage(() => {
      return 1;
    });
    setSortField(() => {
      return updatedFilters;
    });
  };

  const getPayload = (method) => {
    const {
      contributorEmail,
      country,
      invoiceStatus,
      payoutStatus,
      invoiceCreationDate,
      'invoiceCreationDate.customDates': fixedRangeForCreated,
      invoiceLastModifiedDate,
      'invoicePeriod.month': month,
      'invoicePeriod.year': year,
      'invoiceLastModifiedDate.customDates': fixedRangeForUpdated,
      'notes.type': noteType,
      'notes.value': note,
      sortOrder,
    } = filterValues;

    const contains =
      note == undefined || note == null || note == ''
        ? null
        : noteType == undefined ||
            noteType == null ||
            noteType == '' ||
            noteType == 'Contains'
          ? true
          : false;

    let payload_map = {};
    if (method === 'post') {
      payload_map = {
        country,
        invoiceStatus,
        contributorEmail: contributorEmail ? contributorEmail.split(',') : [],
        month,
      };
    } else {
      payload_map = {
        contributorEmail,
        country: country?.filter((i) => i).join(';'),
        invoiceStatus: invoiceStatus?.filter((i) => i).toString(),
        month: month?.filter((i) => i).toString(),
      };
    }

    return {
      searchTerm: searchTerm,
      year: year,
      fixedRangeForCreated: invoiceCreationDate,
      createdStartDate: fixedRangeForCreated?.[0],
      createdEndDate: fixedRangeForCreated?.[1],
      fixedRangeForUpdated: invoiceLastModifiedDate,
      updatedStartDate: fixedRangeForUpdated?.[0],
      updatedEndDate: fixedRangeForUpdated?.[1],
      note,
      contains,
      ...payload_map,
      page: currentPage,
      size: pageSize,
      order: sortOrder,
      [sortField?.field]: sortField?.order,
    };
  };

  const handleInvoices = (rest, from) => {
    setTableLoader(true);

    let payload = getPayload('get');
    payload = { ...payload, ...rest };

    getInvoices(payload)
      .then((data) => {
        if (from === 'clear' || Object.keys(invoices).length === 0) {
          setInvoices({
            [`${data.pagination.currentPage}`]: transformData(data),
          });
        } else {
          setInvoices({
            ...invoices,
            [`${data.pagination.currentPage}`]: transformData(data),
          });
        }

        setTotalRecords(data?.pagination?.totalRows || 0);
        setNullInvoicePeriodCount(
          data?.pagination?.nullInvoicePeriodCounts || 0,
        );
      })
      .catch(() => {
        setError(true);
        handleMessage('error', 'Unable to fetch invoices');
      })
      .finally(() => {
        setTableLoader(false);
        setPageLoader(false);
      });
  };

  const invoices_locked_status = [
    'Ready for Payout',
    'Added to Payout',
    'Payout Pending',
    'Payout Failure',
    'Payout Completed',
    'Voided',
  ];

  const transformData = (data) => {
    const { rows, columns } = data;
    let map = rows.map((row) => {
      return columns.reduce((obj, col, index) => {
        obj[col] = row[index];
        obj['key'] = obj['invoiceId'];
        return obj;
      }, {});
    });

    map = map.map((invoice) => {
      return {
        ...invoice,
        invoiceStatusType: getStatusType(invoice['invoiceStatus']),
        payoutStatusType: getStatusType(invoice['payoutStatus']),
        isLocked: invoices_locked_status.includes(invoice['invoiceStatus']),
        createdAt: invoice['createdAt'].split('T')[0],
        updatedAt: invoice['updatedAt'].split('T')[0],
      };
    });
    return map;
  };

  const handleRowSelection = (keys, selectedRecords) => {
    setSelectedRowKeys(keys);
    setselectedRecords(selectedRecords);
  };
  const handleApplyFilters = (rest) => {
    setOpenFilters(false);
    setTableId(() => tableId + 1);
    setSelectedRowKeys(() => {
      return [];
    });
    setselectedRecords(() => {
      return [];
    });
    setCurrentData(() => {
      return [];
    });
    setInvoices(() => {
      return {};
    });

    setCurrentPage(() => {
      return 1;
    });
    if (currentPage !== 1) {
      setCurrentPage(() => {
        return 1;
      });
    } else {
      handleInvoices(rest, 'clear');
    }
  };
  //
  const handleInvoiceStatusUpdate = (type, rowId, status, period) => {
    setOpenUpdateModal(false);
    setFullScreenLoader(true);

    let keys = [];
    let filter = null;
    let is_query = false;
    let payload = { status: status };
    if (status === 'Ready for Payout') {
      payload = {
        ...payload,
        invoiceMonth: period.month,
        invoiceYear: period.year,
      };
    }

    if (type === 'single') {
      keys = [rowId];
    } else if (type === 'selected') {
      keys = selectedRowKeys;
    } else if (type === 'query') {
      is_query = true;
      filter = getPayload('post');
    }

    updateInvoiceStatus(keys, filter, payload)
      .then((res) => {
        if (res === LOCK_MSG) {
          handleMessage(
            'error',
            'System is Locked for any updates,Please try after some time.',
          );
        } else {
          if (keys.length <= 10 && !is_query) {
            if (res?.failedInvoiceIds?.length > 0) {
              handleMessage(
                'error',
                `Couldn't update ${res?.failedInvoiceIds?.length} invoices. `,
              );
            } else if (res?.successfulInvoices > 0) {
              handleMessage('success', 'Successfully updated invoice statuses');
            }
          } else {
            handleMessage(
              'success',
              'Updating Status is in progress,Please check the batch updates.',
            );
          }
          handleApplyFilters();
        }
      })
      .catch(() => {
        handleMessage('error', 'Failed to update invoice statuses');
      })
      .finally(() => {
        setFullScreenLoader(false);
      });
  };

  const handleCreatePayout = (payload) => {
    setAction(true);
    setFullScreenLoader(true);
    createPayoutEvent()
      .then((res) => {
        if (res?.status === 200) {
          if (res?.data === LOCK_MSG) {
            handleMessage(
              'error',
              'System is Locked for any updates,Please try after some time',
            );
          } else {
            handleMessage('success', 'payouts creation event triggered.');
          }
        } else {
          handleMessage('error', 'Failed to trigger create payouts event');
          console.log(res.status);
        }
      })
      .catch(() => {
        handleMessage('error', 'Failed to trigger create payouts event');
      })
      .finally(() => {
        setFullScreenLoader(false);
        setAction(false);
      });
  };

  const getOverrideErrorMessage = (res) => {
    const data = res?.data;
    if (typeof data === 'string' && data) {
      return data;
    }
    return data?.errorMessage || 'Failed to generate payout with overrides';
  };

  const handleGeneratePayoutOverride = (invoiceId, payload) => {
    setAction(true);
    return generatePayoutWithOverride(invoiceId, payload)
      .then((res) => {
        if (!res || res.status >= 400) {
          handleMessage('error', getOverrideErrorMessage(res));
          return false;
        }
        if (res.data === LOCK_MSG) {
          handleMessage(
            'error',
            'System is Locked for any updates,Please try after some time',
          );
          return false;
        }
        handleMessage(
          'success',
          `Payout ${res?.data?.payoutId} created for invoice ${invoiceId}.`,
        );
        handleApplyFilters();
        return true;
      })
      .catch(() => {
        handleMessage('error', 'Failed to generate payout with overrides');
        return false;
      })
      .finally(() => {
        setAction(false);
      });
  };

  const handleExportCSV = async () => {
    setExportLoader(true);
    try {
      const payload = getPayload('get');
      await getInvoiceCSV(payload);
      handleMessage(
        'success',
        'Invoice CSV file creation is in progress. Please check the batch updates.',
      );
    } catch {
      handleMessage('error', 'Unable to create Invoice CSV file');
    } finally {
      setExportLoader(false);
    }
  };

  useEffect(() => {
    handleInvoices();
  }, [sortField]);

  const handleSearch = (v) => {
    setSearchValue(v);
    if (v.length > 2) {
      setSearchLoader(true);
      let payout = getPayload('get');
      payout = { ...payout, searchTerm: v };
      getSearch(payout)
        .then((data) => {
          const options = data.map((d) => ({
            key: d,
            value: d,
          }));
          setSearchOptions(options);
        })
        .catch(() => {
          handleMessage('error', 'Unable to fetch Payouts');
        })
        .finally(() => {
          setSearchLoader(false);
        });
    } else {
      setSearchOptions([]);
    }
  };

  const handleSearchKey = (v) => {
    setSearchTerm(v || '');
    setSearchDropdown(false);
    // handleInvoices({ searchTerm: v || '' });
    handleApplyFilters({ searchTerm: v || '' });
  };

  const TableHeader = () => (
    <div>
      <div
        style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '-16px',
          padding: '16px',
          backgroundColor: colors.colorSplit,
        }}
      >
        <div style={{ gap: 16, display: 'flex' }}>
          <CustomButton
            onClick={() => setOpenUpdateModal(true)}
            variant="outlined"
            type="default"
            size="middle"
          >
            <SyncOutlined />
            Update Statuses & Notes
          </CustomButton>
          <CustomButton
            variant="outlined"
            type="default"
            size="middle"
            onClick={() => handleExportCSV()}
            loading={exportLoader}
          >
            <ExportOutlined />
            Export CSV
          </CustomButton>
        </div>
        <div
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <CustomButton
            shape="round"
            type="default"
            variant="outlined"
            icon={<FilterOutlined />}
            size="large"
            onClick={() => setOpenFilters(true)}
          >
            Filter
          </CustomButton>
          <ConfigProvider
            theme={{
              token: {
                borderRadius: 100,
              },
            }}
          >
            <AutoComplete
              popupMatchSelectWidth={252}
              style={{
                width: 300,
              }}
              options={searchValue.length >= 3 && searchOptions}
              notFoundContent={
                (searchValue.length < 3 && 'Enter 3 letters for dropdown') ||
                (!searchLoader && searchOptions.length == 0 && 'No data found')
              }
              open={searchDropdown}
              onBlur={() => setSearchDropdown(false)}
              onSelect={(e) => {
                setSearchValue(e);
                setSearchDropdown(false);
              }}
              variant="borderless"
              size="large"
            >
              <div>
                <Input.Search
                  size="large"
                  value={searchValue}
                  onSearch={handleSearchKey}
                  onClear={handleSearchKey}
                  onClick={() => setSearchDropdown(true)}
                  allowClear
                  placeholder="Search by invoice id"
                  enterButton={
                    <Button
                      type="primary"
                      loading={searchLoader}
                      icon={<SearchOutlined />}
                    />
                  }
                  variant="borderless"
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    backgroundColor: colors.colorWhite,
                    borderRadius: 20,
                  }}
                />
              </div>
            </AutoComplete>
          </ConfigProvider>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {contextHolder}
      <InvoiceActions
        key={totalRecords}
        n={selectedRowKeys.length}
        count={totalRecords}
        nullInvoicePeriodCount={nullInvoicePeriodCount}
        onOk={handleInvoiceStatusUpdate}
        openUpdateModal={openUpdateModal}
        setUpdateModalOpen={setOpenUpdateModal}
        setCreatePayoutModalOpen={setCreatePayoutModalOpen}
        createPayoutModalOpen={createPayoutModalOpen}
        selectedRecords={selectedRecords}
        handleCreatePayout={handleCreatePayout}
      />
      <Table
        key={tableId}
        title={TableHeader}
        loading={tableLoader}
        data={currentData}
        handleRowSelection={handleRowSelection}
        selectedRowKeys={selectedRowKeys}
        handleInvoiceStatusUpdate={handleInvoiceStatusUpdate}
        handleGeneratePayoutOverride={handleGeneratePayoutOverride}
        handleApplySort={handleApplySort}
        sortField={sortField}
      />
      <Pagination
        style={{
          padding: '16px 0',
          display: 'flex',
          justifyContent: 'end',
        }}
        pageSize={pageSize}
        current={currentPage}
        total={totalRecords}
        showSizeChanger={true}
        onChange={handlePageChange}
        onShowSizeChange={handlePageSizeChange}
      />
      <FiltersPane
        openFilters={openFilters}
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        setOpenFilters={setOpenFilters}
        handleApply={handleApplyFilters}
      />
      <Spin fullscreen={true} spinning={fullScreenLoader} />
    </>
  );
};

export default Invoices;
